import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { AlertMessage } from '@/components/ui/AlertMessage';

interface LoginFormProps {
  customerId?: string;
  email: string;
  password: string;
  loading: boolean;
  error?: string;
  success?: string;
  showPasswordFallback?: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onLogin: (e: React.FormEvent) => void;
  onGoogleLogin: () => void;
  onBack: () => void;
  onSignupClick: () => void;
  onPasswordFallbackClose?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  customerId,
  email,
  password,
  loading,
  error,
  success,
  showPasswordFallback,
  onEmailChange,
  onPasswordChange,
  onLogin,
  onGoogleLogin,
  onBack,
  onSignupClick,
  onPasswordFallbackClose,
}) => {
  const navigate = useNavigate();
  
  return (
    <Card className="bg-white shadow-2xl border-0">
      <CardHeader className="pt-6">
        <CardTitle className="text-gray-900 text-xl">로그인</CardTitle>
        <CardDescription className="text-gray-600">
          이메일과 비밀번호를 입력하거나 Google로 로그인하세요
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pb-6">
        {error && (
          <AlertMessage 
            type="error" 
            message={error} 
            onClose={() => {}} 
          />
        )}
        {showPasswordFallback && (
          <div className="bg-yellow-50 border-2 border-yellow-300 text-yellow-800 px-4 py-3 rounded-md shadow-sm">
            <div className="flex flex-col space-y-3">
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="font-medium">Google 연동에 실패했습니다</p>
                  <p className="text-sm mt-1">
                    이메일/비밀번호로 로그인하시겠습니까?<br/>
                    <span className="text-xs text-yellow-600">(로그인 성공 시 로그인 유형이 ID/PW로 변경됩니다)</span>
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onPasswordFallbackClose}
                  className="flex-1"
                >
                  ID/PW로 로그인
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onPasswordFallbackClose}
                  className="flex-1"
                >
                  취소
                </Button>
              </div>
            </div>
          </div>
        )}
        {success && (
          <AlertMessage 
            type="success" 
            message={success} 
            onClose={() => {}} 
          />
        )}
        {customerId && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded text-sm flex items-center justify-between gap-2">
            <span className="whitespace-nowrap">고객사 코드: <strong>{customerId}</strong></span>
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-blue-700 underline whitespace-nowrap flex-shrink-0"
              onClick={onBack}
            >
              변경
            </Button>
          </div>
        )}

        <form onSubmit={onLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="login-email">이메일</Label>
            <Input
              id="login-email"
              name="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="이메일을 입력하세요"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="login-password">비밀번호</Label>
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-sm text-indigo-600 hover:text-indigo-800"
                onClick={() => {
                  // 비밀번호 찾기 페이지로 이동 (또는 모달 열기)
                  navigate('/reset-password');
                }}
                disabled={loading}
                tabIndex={-1}
              >
                비밀번호 찾기
              </Button>
            </div>
            <Input
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder="비밀번호를 입력하세요"
              disabled={loading}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
          >
            {loading ? '로그인 중...' : '로그인'}
          </Button>
        </form>
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">또는</span>
          </div>
        </div>
        
        <Button 
          type="button"
          onClick={onGoogleLogin}
          className="w-full bg-white text-gray-900 border border-gray-300 hover:bg-gray-50"
          disabled={loading}
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Google로 로그인
        </Button>
        
        <div className="flex items-center justify-between">
          <Button 
            type="button" 
            variant="link"
            className="text-sm"
            onClick={onSignupClick}
            disabled={loading}
          >
            회원가입
          </Button>
          
          <Button 
            type="button" 
            variant="link"
            className="text-sm"
            onClick={onBack}
            disabled={loading}
          >
            ← 이전
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

